import { MapWrapper } from '@openlayers/MapWrapper';

// ol imports
import OlSnap from 'ol/interaction/Snap';
import OlCollection from 'ol/Collection';
import Feature from 'ol/Feature';
import OlModify from 'ol/interaction/Modify';
import * as olEventsCondition from 'ol/events/condition';
import VectorSource from 'ol/source/Vector';
import { LStorage } from '@shared/Storage';
import VectorLayer from 'ol/layer/Vector';
import * as olStyle from 'ol/style';
import { LineString, MultiLineString as OlMultiLineString } from 'ol/geom';
import { makeObservable, observable } from 'mobx';
import { transform as olTransform } from 'ol/proj';


export class MeasureToolInteractions implements MapTool {
    modifyInteraction: OlModify;
    @observable modifyInteractionUserEnabled: boolean = LStorage.getOrDefault('modifyInteractionUserEnabled', true);

    snapModelsInteraction: OlSnap;
    @observable snapModelsInteractionUserEnabled: boolean = LStorage.getOrDefault('snapModelsInteractionUserEnabled', true);

    snapGonsInteraction: OlSnap;
    @observable snapGonsInteractionUserEnabled: boolean = LStorage.getOrDefault('snapGonsInteractionUserEnabled', true);
    @observable gonsInterval = 100;

    gonsSnapLayer: VectorLayer<any> = new VectorLayer({
        source: new VectorSource(),
        style: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: 'rgba(0,0,0,0.3)',
                width: 1,
            }),
        })
    })

    constructor(
        public mapWrapper: MapWrapper,
        public drawSources: VectorSource[]
    ) {
        makeObservable(this);
    }

    // activated via toolbar
    activate = () => {
        if (this.modifyInteractionUserEnabled) {
            this.addModifyInteraction();
        }

        if (this.snapModelsInteractionUserEnabled) {
            this.addSnapModelsInteraction();
        }

        if (this.snapGonsInteractionUserEnabled) {
            this.addSnapGonsInteraction();
        }

        document.addEventListener('keydown', this.handleKeyboardEvent);
    }

    // deactivated via toolbar
    deactivate = () => {
        this.removeModifyInteraction();
        this.removeSnapModelsInteraction();
        this.removeSnapGonsInteraction();

        document.removeEventListener('keydown', this.handleKeyboardEvent);
    }

    handleKeyboardEvent = (event: KeyboardEvent) => {
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            return;
        }

        if (event.key === 'm') {
            this.toggleModifyUserEnabled();
        }
        if (event.key === 's') {
            this.toggleSnapModelsUserEnabled();
        }
        if (event.key === 'g') {
            this.toggleSnapGonsUserEnabled();
        }
    }


    // MODIFY

    toggleModifyUserEnabled = () => {
        try {
            this.modifyInteractionUserEnabled = !this.modifyInteractionUserEnabled;
            this.persistSettings();

            if (this.modifyInteractionUserEnabled) {
                this.addModifyInteraction();
            } else {
                this.removeModifyInteraction();
            }
        }
        catch (e) {
            console.log('e:', e);
        }
    }

    addModifyInteraction = () => {
        if (this.modifyInteraction) {
            this.removeModifyInteraction();
        }
        const featuresCol: OlCollection<Feature> = new OlCollection();
        this.drawSources.flatMap(s => s.getFeatures()).forEach(f => featuresCol.push(f));

        this.modifyInteraction = new OlModify({
            features: featuresCol,
            condition: event => olEventsCondition.noModifierKeys(event),
            deleteCondition: event => olEventsCondition.doubleClick(event)
        })
        this.mapWrapper.getInstance().addInteraction(this.modifyInteraction);
    }

    removeModifyInteraction = () => {
        if (this.modifyInteraction) {
            this.mapWrapper.getInstance().removeInteraction(this.modifyInteraction);
            this.modifyInteraction = null;
        }
    }


    // SNAP MODELS
    currentSnapPoint: Feature = null;
    initialAngleOffset = 0;

    addSnapModelsInteraction = () => {
        const features = [
            ...this.mapWrapper.featureLayers.logpoints.getVisibleFeatures(),
            ...this.mapWrapper.featureLayers.models.layerSource.getFeatures(),
            ...this.mapWrapper.featureLayers.alignments.layerSource.getFeatures(),
            ...this.mapWrapper.featureLayers.documents.layerSource.getFeatures(),
            ...this.mapWrapper.featureLayers.images.layerSource.getFeatures(),
            ...this.mapWrapper.featureLayers.pipes.layerSource.getFeatures(),
            ...this.mapWrapper.featureLayers.stringlines.layerSource.getFeatures(),
            ...this.mapWrapper.featureLayers.points.layerSource.getFeatures()
        ];
        const featuresCol: OlCollection<Feature> = new OlCollection();
        features.forEach(f => featuresCol.push(f));
        this.drawSources.flatMap(s => s.getFeatures()).forEach(f => featuresCol.push(f));

        this.snapModelsInteraction = new OlSnap({
            features: featuresCol
        });
        this.snapModelsInteraction.on('snap' as any, evt => {
            if ((evt as any).feature?.getGeometry().getType() === 'Point') {
                this.currentSnapPoint = (evt as any).feature;
            } else {
                this.currentSnapPoint = null;
            }

            if (!(evt as any).segment || !this.snapGonsInteractionUserEnabled) {
                this.initialAngleOffset = null;
            } else {
                const centerPoint = (evt as any).vertex;
                const endPoint = (evt as any).segment[0];
                const angleOffsetRad = Math.atan2(endPoint[1] - centerPoint[1], endPoint[0] - centerPoint[0]);
                this.initialAngleOffset = parseFloat((angleOffsetRad * 180 / Math.PI).toFixed(1));
            }
        })

        // this.snapModelsInteraction.on('unsnap') - can be used for optimization but we need to wait for openlayers v10.5

        this.mapWrapper.getInstance().addInteraction(this.snapModelsInteraction);
    }

    removeSnapModelsInteraction = () => {
        if (this.snapModelsInteraction) {
            this.mapWrapper.getInstance().removeInteraction(this.snapModelsInteraction);
            this.snapModelsInteraction = null;
        }
    }

    toggleSnapModelsUserEnabled = () => {
        try {
            this.snapModelsInteractionUserEnabled = !this.snapModelsInteractionUserEnabled;
            this.persistSettings();

            if (this.snapModelsInteractionUserEnabled) {
                this.addSnapModelsInteraction();
            } else {
                this.removeSnapModelsInteraction();
            }
        }
        catch (e) {
            console.log('e:', e);
        }
    }

    // GONS SNAP

    addSnapGonsInteraction = () => {
        this.snapGonsInteraction = new OlSnap({
            source: this.gonsSnapLayer.getSource()
        });
        this.mapWrapper.getInstance().addInteraction(this.snapGonsInteraction);
        this.mapWrapper.addLayer(this.gonsSnapLayer, 1111, 'gons-snap');
    }

    removeSnapGonsInteraction = () => {
        if (this.snapGonsInteraction) {
            this.mapWrapper.getInstance().removeInteraction(this.snapGonsInteraction);
            this.snapGonsInteraction = null;
            this.mapWrapper.removeLayer(this.gonsSnapLayer);
        }
    }

    toggleSnapGonsUserEnabled = () => {
        try {
            this.snapGonsInteractionUserEnabled = !this.snapGonsInteractionUserEnabled;
            this.persistSettings();

            if (this.snapGonsInteractionUserEnabled) {
                this.addSnapGonsInteraction();
            } else {
                this.removeSnapGonsInteraction();
            }
        }
        catch (e) {
            console.log('e:', e);
        }
    }

    setGons = e => {
        e.preventDefault();
        const gons = window.prompt('Set gons, 10-100', String(this.gonsInterval));
        this.gonsInterval = utils.clamp(10, parseInt(gons), 100);
    }

    addGonsSnap = (feature: Feature) => {
        if (!this.snapGonsInteractionUserEnabled || feature.get('finished')) {
            return;
        }
        this.removeGonsSnap();

        const sourceProj = this.mapWrapper.getProjection().getCode();
        let coords = (feature.getGeometry() as any).getCoordinates();

        if (feature.getGeometry().getType() === 'Polygon') {
            coords = coords[0].reverse();
            if (coords.length > 2) {
                coords = coords.slice(1);
            }
        } else {
            coords = coords.reverse();
        }

        if (coords[1] && coords[0][0] === coords[1][0] && coords[0][1] === coords[1][1]) {
            coords = coords.slice(1);
        }

        const centerPoint = coords[0];
        const crossCoords = [];
        let angleOffset = 0;
        if (this.initialAngleOffset) {
            angleOffset = this.initialAngleOffset;
            this.initialAngleOffset = null;
        } else {
            const endPoint = coords[1];
            if (endPoint) {
                const angleOffsetRad = Math.atan2(endPoint[1] - centerPoint[1], endPoint[0] - centerPoint[0]);
                angleOffset = parseFloat((angleOffsetRad * 180 / Math.PI).toFixed(1));
            }
        }

        const centerPointOffset = offsetNE(centerPoint[0], centerPoint[1], 0, 5000, sourceProj);
        let angle = 0;
        while (angle <= 360) {
            angle += (this.gonsInterval * 0.9);
            const geometry = new LineString([
                [centerPoint[0], centerPoint[1]],
                [centerPointOffset[0], centerPointOffset[1]],
            ]);
            geometry.rotate((angle + angleOffset) * Math.PI / 180, centerPoint);
            crossCoords.push(geometry.getCoordinates());
        }

        const crossGeom = new OlMultiLineString(crossCoords);
        const gonsFeature = new Feature({
            geometry: crossGeom
        });
        gonsFeature.set('id', 'gons-cross');

        if (!this.gonsSnapLayer?.getSource()) {
            this.addSnapGonsInteraction();
        }
        this.gonsSnapLayer.getSource().clear()
        this.gonsSnapLayer.getSource().addFeature(gonsFeature);
    }

    removeGonsSnap = () => {
        this.gonsSnapLayer.getSource().clear();
    }

    // SHARED

    addFeature = (feature: Feature) => {
        if (this.snapModelsInteraction) {
            this.snapModelsInteraction.addFeature(feature);
        }
        if (this.modifyInteraction) {
            (this.modifyInteraction as any).addFeature_(feature);
        }
        this.removeGonsSnap();
    }

    removeFeature = (feature: Feature) => {
        if (this.snapModelsInteraction) {
            this.snapModelsInteraction.removeFeature(feature);
        }
        if (this.modifyInteraction) {
            (this.modifyInteraction as any).removeFeature_(feature);
        }
        this.removeGonsSnap();
    }

    persistSettings = () => {
        LStorage.setItem('snapModelsInteractionUserEnabled', this.snapModelsInteractionUserEnabled);
        LStorage.setItem('snapGonsInteractionUserEnabled', this.snapGonsInteractionUserEnabled);
        LStorage.setItem('modifyInteractionUserEnabled', this.modifyInteractionUserEnabled);
    }
}

function offsetNE(n: number, e: number, x: number, y: number, sourceProj: string) {
    const latLon = olTransform([n, e], sourceProj, 'EPSG:4326');
    const result = offsetHaversine(latLon[1], latLon[0], x, y);
    return olTransform([result[1], result[0]], 'EPSG:4326', sourceProj);
}

function offsetHaversine(aLat: number, aLng: number, x: number, y: number) {
    const R = 6378137;
    const dLat = y / R;
    const dLng = x / (R * Math.cos(aLat * Math.PI / 180.0));
    return [
        aLat + dLat * 180.0 / Math.PI,
        aLng + dLng * 180.0 / Math.PI
    ];
}
